import { For, Show, createMemo } from 'solid-js'
import type { MeasurementEntry, MetricDefinition } from '@fasting-timer/shared'

import { useI18n } from '../../i18n'
import { deleteMeasurement } from '../../state/measurementsStore'
import { measurementsPage, setMeasurementsPage } from '../../state/measurementsUiStore'
import { openMeasurementEditModal } from '../../state/measurementEditStore'
import EntryActions from '../ui/EntryActions'
import { describeEntry, formatEntryTimestamp } from './measurementFormat'

const ENTRIES_PER_PAGE = 5

type MeasurementListProps = {
   definition: MetricDefinition
   /** Entries to list, newest first. */
   entries: MeasurementEntry[]
}

/**
 * Renders the paginated entry list with edit/delete actions.
 *
 * @param props - The metric definition and entries.
 * @returns The list element.
 */
const MeasurementList = (props: MeasurementListProps) => {
   const t = useI18n()
   const pageCount = createMemo(() =>
      Math.max(1, Math.ceil(props.entries.length / ENTRIES_PER_PAGE)),
   )
   const currentPage = createMemo(() => Math.min(measurementsPage(), pageCount() - 1))
   const pagedEntries = createMemo(() => {
      const start = currentPage() * ENTRIES_PER_PAGE
      return props.entries.slice(start, start + ENTRIES_PER_PAGE)
   })
   const paginationLabel = createMemo(() => {
      const total = props.entries.length
      if (total <= ENTRIES_PER_PAGE) {
         return ''
      }
      return t('Showing {start}-{end} of {total}', {
         start: currentPage() * ENTRIES_PER_PAGE + 1,
         end: Math.min((currentPage() + 1) * ENTRIES_PER_PAGE, total),
         total,
      })
   })

   return (
      <div>
         <div id="measurement-entry-list" class="space-y-2">
            <Show
               when={pagedEntries().length > 0}
               fallback={
                  <p class="text-sm text-ink-faint">
                     {t('No entries available for this period.')}
                  </p>
               }
            >
               <For each={pagedEntries()}>
                  {entry => (
                     <div class="flex items-center justify-between gap-3 py-2 lg:px-3 lg:bg-surface-raised lg:border lg:border-edge-soft lg:rounded-lg">
                        <div>
                           <p class="text-sm font-semibold text-ink">
                              {describeEntry(entry, props.definition, [
                                 props.definition.primary,
                                 ...('effort' in props.definition.derived ?
                                    ['effort']
                                 :  []),
                              ]).join(' · ')}
                           </p>
                           <p class="text-xs text-ink-faint">
                              {formatEntryTimestamp(entry)}
                           </p>
                        </div>
                        <EntryActions
                           onEdit={() => openMeasurementEditModal(entry.entryId)}
                           onDelete={() => deleteMeasurement(entry.entryId)}
                        />
                     </div>
                  )}
               </For>
            </Show>
         </div>
         <div class="flex items-center justify-between text-xs text-ink-faint mt-4">
            <span>{paginationLabel()}</span>
            <Show when={props.entries.length > ENTRIES_PER_PAGE}>
               <div class="flex gap-2">
                  <button
                     type="button"
                     onClick={() => setMeasurementsPage(currentPage() - 1)}
                     disabled={currentPage() === 0}
                     class="px-2 py-1 rounded-md border border-edge bg-surface-raised text-ink-soft disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                     {t('Prev')}
                  </button>
                  <button
                     type="button"
                     onClick={() => setMeasurementsPage(currentPage() + 1)}
                     disabled={currentPage() >= pageCount() - 1}
                     class="px-2 py-1 rounded-md border border-edge bg-surface-raised text-ink-soft disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                     {t('Next')}
                  </button>
               </div>
            </Show>
         </div>
      </div>
   )
}

export default MeasurementList
